import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import AppConst from '../apis/appconst';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';

/* ============================================================
 *  PATIENT TYPE
 * ============================================================ */

export const PATIENT_TYPES = ['IPD', 'OPD', 'EMERGENCY', 'CASUALTY'];

// Accepts anything the opening page sends ("opd", "Emergency",
// "Day Care", "in-patient"...) and returns one of the four types.
export const normalizePatientType = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    if (!s) return '';
    if (s.includes('emerg')) return 'EMERGENCY';
    if (s.includes('casual')) return 'CASUALTY';
    if (s.includes('ipd') || s.includes('inpatient') || s.includes('in-patient')) return 'IPD';
    if (s.includes('opd') || s.includes('outpatient') || s.includes('out-patient')
        || s.includes('day care') || s.includes('daycare')) return 'OPD';
    return '';
};

function ProcedureBillModal({
    patientData,
    initialBillId = null,     // open straight into an existing bill (from the list page)
    onClose,
    onSaved,                  // called after save/pay/delete so the list can refresh
    image,
    watermarkImage,
    footerImage
}) {
    // Type the opening page already knows — locks the field when present
    const autoPatientType = normalizePatientType(
        patientData?.patient_type || patientData?.bill_source || patientData?.source || ''
    );

    // No patient handed in => standalone "Add" from the procedure list page
    const isStandalone = !(
        patientData?.ipd_bill_uhid || patientData?.opd_uhid || patientData?.ot_uhid_no
    );

    const [formData, setFormData] = useState({
        procedure_bill_id: '',
        ipd_bill_id: patientData?.ipd_bill_id || patientData?.opd_id || '',
        ipd_bill_uhid: patientData?.ipd_bill_uhid || patientData?.opd_uhid || patientData?.ot_uhid_no || '',
        ipd_no: patientData?.ipd_no || patientData?.opd_id || '',
        patient_type: autoPatientType,
        patient_name: patientData?.ipd_bill_patient_name || patientData?.opd_patient_name || '',
        patient_age: patientData?.ipd_bill_patient_age || patientData?.opd_guardien_age || patientData?.opd_patient_age || '',
        patient_gender: patientData?.ipd_bill_patient_gender || patientData?.opd_patient_gender || '',
        patient_phone: patientData?.ipd_bill_patient_phone || patientData?.opd_patient_mobile || '',
        patient_address: patientData?.ipd_bill_patient_address || patientData?.opd_address || '',
        bill_date: new Date().toISOString().slice(0, 16),
        created_at: '',
        total_amount: 0,
        discount_percent: 0,
        discount_amount: 0,
        final_amount: 0,
        payment_mode: 'Cash',
        remarks: '',
        items: [],
        services: [],
        payment_status: 'Pending',
        paid_amount: 0
    });

    const [searchKey, setSearchKey] = useState('');
    const [saving, setSaving] = useState(false);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [isLoadingServices, setIsLoadingServices] = useState(false);
    const [headerImageUrl, setHeaderImageUrl] = useState(image || '');
    const [isLoadingBill, setIsLoadingBill] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentData, setPaymentData] = useState({
        amount: 0,
        payment_mode: 'Cash',
        received_by: '',
        remarks: ''
    });

    // standalone patient lookup
    const [patientSearch, setPatientSearch] = useState('');
    const [patientResults, setPatientResults] = useState([]);
    const [searchingPatient, setSearchingPatient] = useState(false);
    const patientDebounceRef = useRef(null);

    const modalBodyRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const [hasPaymentTable, setHasPaymentTable] = useState(false);
    const [billFetched, setBillFetched] = useState(false);
    const [billList, setBillList] = useState([]);
    const [loadingBillList, setLoadingBillList] = useState(false);

    /* ============================================================
     *  LOAD / RESET
     * ============================================================ */

    // ── Load ONE bill by its unique procedure_bill_id (edit mode) ──
    const loadBillById = async (billId) => {
        if (!billId) return;
        setIsLoadingBill(true);
        try {
            const fd = new FormData();
            fd.append('procedure_bill_id', billId);

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getProcedureBillUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setHasPaymentTable(!!response?.data?.has_payment_table);

            if (response?.data?.status && response.data.bill) {
                const bill = response.data.bill;
                const items = response.data.items || [];
                const payments = response.data.payments || [];
                setBillFetched(true);

                const mappedItems = items.map(item => ({
                    service_id: item.service_id || item.cat_id || '',
                    service_name: item.service_name || item.cat_name || '',
                    qty: Number(item.qty || item.quantity || 1),
                    unit_price: Number(item.unit_price || item.price || 0),
                    discount: Number(item.discount || 0),
                    final_price: Number(item.final_price || item.amount || 0)
                }));

                const total = mappedItems.reduce((s, it) => s + Number(it.final_price || 0), 0);
                const discountPercent = Number(bill.discount_percent || 0);
                const discountAmount = Number(bill.discount_amount || 0);
                const finalAmt = total - discountAmount;

                let paidAmount = Number(bill.paid_amount || 0);
                if (paidAmount === 0 && payments.length > 0) {
                    paidAmount = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
                }
                const remainingBalance = Math.max(0, finalAmt - paidAmount);
                const originalFinal = Number(bill.original_final_amount || 0) || finalAmt;
                const paymentStatus = bill.payment_status ||
                    (remainingBalance <= 0 ? 'Paid' : (paidAmount > 0 ? 'Partial' : 'Pending'));

                const loaded = {
                    procedure_bill_id: bill.procedure_bill_id || billId,
                    ipd_bill_id: bill.ipd_bill_id,
                    ipd_bill_uhid: bill.ipd_bill_uhid,
                    ipd_no: bill.ipd_no,
                    // ✅ stored type, falling back to whatever the opener knew
                    patient_type: normalizePatientType(bill.patient_type) || autoPatientType,
                    patient_name: bill.patient_name,
                    patient_age: bill.patient_age,
                    patient_gender: bill.patient_gender,
                    patient_phone: bill.patient_phone,
                    patient_address: bill.patient_address,
                    bill_date: bill.bill_date,
                    created_at: bill.created_at || bill.bill_date || '',
                    total_amount: total.toFixed(2),
                    discount_percent: discountPercent,
                    discount_amount: discountAmount.toFixed(2),
                    final_amount: remainingBalance.toFixed(2),
                    original_final_amount: originalFinal.toFixed(2),
                    payment_mode: bill.payment_mode || 'Cash',
                    remarks: bill.remarks || '',
                    items: mappedItems,
                    payment_status: paymentStatus,
                    paid_amount: paidAmount,
                    payments: payments
                };

                setFormData(prev => ({ ...prev, ...loaded }));
                toast.success('Procedure bill loaded');
                return loaded;
            } else {
                toast.error('Bill not found');
                return null;
            }
        } catch (err) {
            console.error('loadBillById error:', err);
            toast.error('Failed to load bill');
            return null;
        } finally {
            setIsLoadingBill(false);
        }
    };

    // ── Reset to a fresh blank bill (patient header + type stay) ──
    const startNewBill = () => {
        setBillFetched(false);
        setFormData(prev => ({
            ...prev,
            procedure_bill_id: '',
            total_amount: '0.00',
            discount_percent: 0,
            discount_amount: '0.00',
            final_amount: '0.00',
            original_final_amount: '0.00',
            paid_amount: 0,
            payment_status: 'Pending',
            payment_mode: 'Cash',
            remarks: '',
            items: [],
            payments: [],
            created_at: ''
        }));
    };

    // ── Fetch this patient's previous bills (grouped by UHID) ──
    const fetchBillListForPatient = async (uhidOverride = null) => {
        const uhid = uhidOverride
            || formData.ipd_bill_uhid
            || patientData?.ipd_bill_uhid || patientData?.opd_uhid || patientData?.ot_uhid_no || '';
        if (!uhid) { setBillList([]); return []; }

        setLoadingBillList(true);
        try {
            const fd = new FormData();
            fd.append('page_no', 'none');
            fd.append('uhid', uhid);

            const res = await axios.post(
                AppConst.BASE_URL + AppConst.getProcedureBillsListUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            const rows = (res?.data?.status && Array.isArray(res.data.records)) ? res.data.records : [];
            setBillList(rows);
            return rows;
        } catch (err) {
            console.error('fetchBillListForPatient error:', err);
            setBillList([]);
            return [];
        } finally {
            setLoadingBillList(false);
        }
    };

    // ── Entry point on open ──
    const fetchExistingBill = async () => {
        if (initialBillId) {
            // opened from the list page's Edit/Print button
            const loaded = await loadBillById(initialBillId);
            await fetchBillListForPatient(loaded?.ipd_bill_uhid);
            return;
        }
        // Never auto-load a bill. Form starts blank (new bill).
        startNewBill();
        await fetchBillListForPatient();
    };

    useEffect(() => {
        fetchExistingBill();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientData, initialBillId]);

    /* ============================================================
     *  STANDALONE PATIENT LOOKUP
     * ============================================================ */

    const runPatientSearch = (val) => {
        setPatientSearch(val);
        const trimmed = val.trim();

        if (patientDebounceRef.current) clearTimeout(patientDebounceRef.current);

        if (trimmed.length < 3) {
            setPatientResults([]);
            return;
        }

        const isPhone = /^\d{6,}$/.test(trimmed);
        const isUhidLike = /^[A-Za-z]{1,4}\d+/.test(trimmed);

        patientDebounceRef.current = setTimeout(async () => {
            setSearchingPatient(true);
            try {
                const fd = new FormData();
                if (isPhone) fd.append('lab_reg_mobile', trimmed);
                else if (isUhidLike) fd.append('lab_reg_uhid', trimmed);
                else fd.append('lab_reg_name', trimmed);

                const res = await axios.post(
                    AppConst.BASE_URL + AppConst.searchUhidUrlCall,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
                setPatientResults(res?.data?.status ? (res.data.records || []) : []);
            } catch (err) {
                console.error('patient search error:', err);
                setPatientResults([]);
            } finally {
                setSearchingPatient(false);
            }
        }, 400);
    };

    const applyPatient = async (p) => {
        const uhid = (p.lab_reg_uhid || '').trim();

        // the visit number the search result came from:
        // search_uhid returns source_id (opd_id / ipd_id / emergency id),
        // plus ipd_no + ipd_bill_id on IPD rows
        const visitNo = String(
            p.ipd_no ?? p.source_id ?? p.opd_id ?? p.ipd_id ?? p.lab_reg_id ?? ''
        ).trim();

        const foundType = normalizePatientType(p.lab_reg_type || p.source_type);

        setFormData(prev => ({
            ...prev,
            ipd_bill_uhid: uhid,
            ipd_no: visitNo,                                   // ✅ was left blank before
            ipd_bill_id: String(p.ipd_bill_id ?? prev.ipd_bill_id ?? '').trim(),
            patient_name: p.lab_reg_name || '',
            patient_age: p.lab_reg_age_year || p.lab_reg_age || '',
            patient_gender: p.lab_reg_gender || '',
            patient_phone: p.lab_reg_mobile || '',
            patient_address: p.lab_reg_address || '',
            // suggest a type from the source record, user can still change it
            patient_type: prev.patient_type || foundType
        }));
        setPatientSearch('');
        setPatientResults([]);
        toast.success('Patient details loaded');
        await fetchBillListForPatient(uhid);
    };

    /* ============================================================
     *  CONFIG / SERVICES
     * ============================================================ */

    useEffect(() => {
        const fetchHeaderImage = async () => {
            try {
                if (image) {
                    setHeaderImageUrl(image);
                    return;
                }
                const res = await axios.post(
                    `${AppConst.BASE_URL}${AppConst.getconfig}?config_type=lab`
                );
                if (res.data.status && res.data.header_logo) {
                    setHeaderImageUrl(res.data.header_logo);
                }
            } catch (err) {
                console.error('Error fetching header image:', err);
            }
        };
        fetchHeaderImage();
    }, [image]);

    // Prevent mouse wheel from changing number input values
    useEffect(() => {
        const preventWheel = (e) => {
            if (document.activeElement.type === 'number') e.preventDefault();
        };
        document.addEventListener('wheel', preventWheel, { passive: false });
        return () => document.removeEventListener('wheel', preventWheel);
    }, []);

    const fetchServices = async (inputValue = '') => {
        setIsLoadingServices(true);
        try {
            const fd = new FormData();
            fd.append('cat_type', 'service');
            fd.append('page_no', 'none');
            fd.append('search_key', inputValue);
            fd.append('par_by', '1');

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getCatListUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response?.data?.status && Array.isArray(response.data.records)) {
                const services = response.data.records;
                const options = services.map(srv => ({
                    value: srv.cat_id || srv.id,
                    label: srv.cat_name || srv.name,
                    price: Number(srv.cat_price || srv.price || 0),
                    serviceData: srv
                }));
                setServiceOptions(options);
                setFormData(prev => ({ ...prev, services }));
            }
        } catch (err) {
            console.error('Error fetching services:', err);
        } finally {
            setIsLoadingServices(false);
        }
    };

    useEffect(() => {
        fetchServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleServiceSelect = (selectedOption) => {
        if (!selectedOption) return;

        const service = selectedOption.serviceData;
        const price = Number(service.cat_price || service.price || 0);

        const newItem = {
            service_id: service.cat_id || service.id,
            service_name: service.cat_name || service.name,
            qty: 1,
            unit_price: price,
            discount: 0,
            final_price: price
        };

        setFormData(prev => {
            const updatedItems = [...prev.items, newItem];
            const totals = calculateTotals({ ...prev, items: updatedItems });
            return { ...prev, items: updatedItems, ...totals };
        });

        toast.success(`${newItem.service_name} added`);
    };

    /* ============================================================
     *  TOTALS
     * ============================================================ */

    const calculateTotals = (currentData) => {
        const items = currentData.items || [];
        const total = items.reduce((sum, item) => sum + Number(item.final_price || 0), 0);
        const discountPercent = Number(currentData.discount_percent || 0);
        const discountAmtDirect = Number(currentData.discount_amount || 0);

        let discountAmount = 0;
        let finalAmount = 0;

        if (currentData._discountMode === 'amount') {
            discountAmount = discountAmtDirect > total ? total : discountAmtDirect;
        } else {
            discountAmount = (total * discountPercent) / 100;
        }

        finalAmount = total - discountAmount;

        const paidAmount = Number(currentData.paid_amount || 0);
        const remainingBalance = Math.max(0, finalAmount - paidAmount);

        let originalFinalAmount = Number(currentData.original_final_amount || 0);
        if (originalFinalAmount === 0 && finalAmount > 0) {
            originalFinalAmount = finalAmount;
        }
        if (currentData._recalculateOriginal) {
            originalFinalAmount = finalAmount;
            currentData._recalculateOriginal = false;
        }

        return {
            total_amount: total.toFixed(2),
            discount_amount: discountAmount.toFixed(2),
            final_amount: remainingBalance.toFixed(2),
            original_final_amount: originalFinalAmount.toFixed(2),
            paid_amount: paidAmount.toFixed(2)
        };
    };

    const handleItemChange = (index, field, value) => {
        if (modalBodyRef.current) {
            scrollPositionRef.current = modalBodyRef.current.scrollTop;
        }

        setFormData(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index] = { ...updatedItems[index], [field]: value };

            const item = updatedItems[index];
            const qty = Number(item.qty || 0);
            const unitPrice = Number(item.unit_price || 0);
            const discount = Number(item.discount || 0);
            item.final_price = (qty * unitPrice) - discount;

            const total = updatedItems.reduce((sum, it) => sum + Number(it.final_price || 0), 0);
            const discountPercent = Number(prev.discount_percent || 0);
            const discountAmt = (total * discountPercent) / 100;
            const finalAmt = total - discountAmt;
            const paidAmount = Number(prev.paid_amount || 0);
            const remainingBalance = Math.max(0, finalAmt - paidAmount);

            let originalFinal = Number(prev.original_final_amount || 0);
            if (originalFinal === 0 || originalFinal === parseFloat(prev.final_amount) + paidAmount) {
                originalFinal = finalAmt;
            }

            setTimeout(() => {
                if (modalBodyRef.current) {
                    modalBodyRef.current.scrollTop = scrollPositionRef.current;
                }
            }, 0);

            return {
                ...prev,
                items: updatedItems,
                total_amount: total.toFixed(2),
                discount_amount: discountAmt.toFixed(2),
                final_amount: remainingBalance.toFixed(2),
                original_final_amount: originalFinal.toFixed(2)
            };
        });
    };

    const removeItem = (index) => {
        setFormData(prev => {
            const updatedItems = prev.items.filter((_, i) => i !== index);
            const totals = calculateTotals({ ...prev, items: updatedItems });
            return { ...prev, items: updatedItems, ...totals };
        });
    };

    const handleDiscountChange = (value) => {
        const percent = Number(value || 0);
        setFormData(prev => {
            const items = prev.items || [];
            const total = items.reduce((sum, item) => sum + Number(item.final_price || 0), 0);
            const discountAmt = (total * percent) / 100;
            const finalAmt = total - discountAmt;
            const paidAmount = Number(prev.paid_amount || 0);
            const remainingBalance = Math.max(0, finalAmt - paidAmount);

            let originalFinal = Number(prev.original_final_amount || 0);
            if (originalFinal === 0 || originalFinal === parseFloat(prev.final_amount) + paidAmount) {
                originalFinal = finalAmt;
            }

            return {
                ...prev,
                discount_percent: value,
                discount_amount: discountAmt.toFixed(2),
                final_amount: remainingBalance.toFixed(2),
                original_final_amount: originalFinal.toFixed(2),
                _discountMode: 'percent'
            };
        });
    };

    const handleDiscountAmountChange = (value) => {
        const discountAmt = Number(value || 0);
        setFormData(prev => {
            const items = prev.items || [];
            const total = items.reduce((sum, item) => sum + Number(item.final_price || 0), 0);
            const clampedDiscount = discountAmt > total ? total : discountAmt;
            const percent = total > 0 ? ((clampedDiscount / total) * 100) : 0;
            const finalAmt = total - clampedDiscount;
            const paidAmount = Number(prev.paid_amount || 0);
            const remainingBalance = Math.max(0, finalAmt - paidAmount);

            let originalFinal = Number(prev.original_final_amount || 0);
            if (originalFinal === 0 || originalFinal === parseFloat(prev.final_amount) + paidAmount) {
                originalFinal = finalAmt;
            }

            return {
                ...prev,
                discount_percent: percent.toFixed(2),
                discount_amount: value,
                final_amount: remainingBalance.toFixed(2),
                original_final_amount: originalFinal.toFixed(2),
                _discountMode: 'amount'
            };
        });
    };

    /* ============================================================
     *  PAYMENT
     * ============================================================ */

    const handlePayBill = () => {
        if (formData.items.length === 0) {
            toast.error('No items to pay');
            return;
        }

        const dueAmount = parseFloat(formData.final_amount);
        if (dueAmount <= 0) {
            toast.error('Bill is already fully paid');
            return;
        }

        let receivedBy = '';
        let userName = '';
        let userDisplayName = '';

        try {
            const stored = sessionStorage.getItem("ldata");
            if (stored) {
                const u = JSON.parse(stored);
                userName = u.user_name || u.username || u.fname || u.full_name || u.display_name || '';
                const userId = u.user_id || u.id || '';

                if (userName) userDisplayName = userName;
                else if (userId) userDisplayName = `User (ID: ${userId})`;
                else userDisplayName = 'Unknown User';

                receivedBy = userDisplayName;
            } else {
                const userData = sessionStorage.getItem("user_data");
                if (userData) {
                    const u = JSON.parse(userData);
                    userName = u.name || u.user_name || u.username || '';
                    if (userName) receivedBy = userName;
                }
            }
        } catch (e) {
            console.warn("Failed to get user info:", e);
        }

        if (!receivedBy) {
            try {
                const stored = localStorage.getItem("user");
                if (stored) {
                    const u = JSON.parse(stored);
                    receivedBy = u.name || u.user_name || u.username || u.full_name || '';
                }
            } catch (e) {
                console.warn("Failed to get user from localStorage:", e);
            }
        }

        if (!receivedBy) receivedBy = 'System User';

        setPaymentData({
            amount: dueAmount,
            payment_mode: 'Cash',
            received_by: receivedBy,
            remarks: ''
        });
        setShowPaymentModal(true);
    };

    // Admin check (role_id === '1')
    const isAdminUser = (() => {
        try {
            const raw = sessionStorage.getItem('ldata') || localStorage.getItem('ldata');
            if (!raw) return false;
            const p = JSON.parse(raw);
            return String(p?.role_id ?? p?.roleId ?? '') === '1';
        } catch { return false; }
    })();

    const handleDeleteProcedureBill = async (bill) => {
        if (!isAdminUser) {
            toast.error('Only admin can delete.');
            return;
        }
        const billId = bill?.procedure_bill_id;
        if (!billId) {
            toast.error('Invalid bill id');
            return;
        }

        const confirm = await Swal.fire({
            title: 'Delete this procedure bill?',
            html: `<div style="text-align:left">
                     <p><strong>Bill ID:</strong> ${billId}</p>
                     <p><strong>Patient:</strong> ${bill.patient_name || ''}</p>
                     <p><strong>Amount:</strong> ₹${parseFloat(bill.final_amount || bill.original_final_amount || 0).toFixed(2)}</p>
                   </div>
                   <p style="color:#dc3545;margin-top:8px">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Yes, Delete'
        });
        if (!confirm.isConfirmed) return;

        try {
            Swal.fire({ title: 'Deleting...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            const fd = new FormData();
            fd.append('procedure_bill_id', billId);
            try {
                const raw = sessionStorage.getItem('ldata');
                if (raw) {
                    const u = JSON.parse(raw);
                    fd.append('user_id', u.user_id || '');
                    fd.append('user_name', `${u.fname || ''} ${u.lname || ''}`.trim());
                    fd.append('user_role', u.role_id || '');
                }
            } catch { }

            const res = await axios.post(
                AppConst.BASE_URL + 'opd_apis/delete_procedure_bill',
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            Swal.close();

            if (res.data?.status) {
                toast.success(res.data.message || 'Procedure bill deleted');
                if (String(formData.procedure_bill_id) === String(billId)) {
                    startNewBill();
                }
                await fetchBillListForPatient();
                if (typeof onSaved === 'function') onSaved();
            } else {
                toast.error(res.data?.message || 'Delete failed');
            }
        } catch (err) {
            Swal.close();
            console.error('handleDeleteProcedureBill error:', err);
            toast.error('Error deleting procedure bill');
        }
    };

    const processPayment = async () => {
        if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        const dueAmount = parseFloat(formData.final_amount);
        if (parseFloat(paymentData.amount) > dueAmount) {
            toast.error(`Amount cannot exceed due amount of ₹${dueAmount.toFixed(2)}`);
            return;
        }

        const { isConfirmed } = await Swal.fire({
            title: 'Confirm Payment',
            text: `Are you sure you want to process payment of ₹${parseFloat(paymentData.amount).toFixed(2)}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Pay',
            cancelButtonText: 'Cancel'
        });

        if (!isConfirmed) return;

        setSaving(true);
        Swal.fire({ title: 'Processing Payment...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        try {
            const fd = new FormData();
            fd.append('procedure_bill_id', formData.procedure_bill_id);
            fd.append('amount', paymentData.amount);
            fd.append('payment_mode', paymentData.payment_mode);
            fd.append('remarks', paymentData.remarks || '');

            try {
                const stored = sessionStorage.getItem("ldata");
                if (stored) {
                    const u = JSON.parse(stored);
                    fd.append('received_by', u.user_id || '');
                    fd.append('received_by_name', u.user_name || u.username || u.name || '');
                    fd.append('opd_user', u.user_id || '');
                }
            } catch (e) {
                console.warn("attach user failed:", e);
            }

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.payProcedureBillUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            Swal.close();

            if (response?.data?.status) {
                toast.success('Payment processed successfully');
                setShowPaymentModal(false);

                const newPaidAmount = parseFloat(formData.paid_amount || 0) + parseFloat(paymentData.amount);
                const newFinalAmount = parseFloat(formData.final_amount) - parseFloat(paymentData.amount);
                const finalAmount = Math.max(0, newFinalAmount);
                const newPaymentStatus = finalAmount <= 0 ? 'Paid' : 'Partial';

                setFormData(prev => ({
                    ...prev,
                    payment_status: newPaymentStatus,
                    paid_amount: newPaidAmount,
                    final_amount: finalAmount.toFixed(2)
                }));

                // reload the saved bill so everything matches the server
                if (formData.procedure_bill_id) await loadBillById(formData.procedure_bill_id);
                await fetchBillListForPatient();
                if (typeof onSaved === 'function') onSaved();

            } else {
                toast.error(response?.data?.message || 'Failed to process payment');
            }
        } catch (err) {
            Swal.close();
            console.error('Payment error:', err);
            toast.error('Failed to process payment');
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
     *  SAVE
     * ============================================================ */

    const handleSave = async () => {
        // ✅ patient type is mandatory
        if (!formData.patient_type) {
            toast.error('Please select the patient type (IPD / OPD / Emergency / Casualty)');
            if (modalBodyRef.current) modalBodyRef.current.scrollTop = 0;
            return;
        }

        if (!String(formData.patient_name || '').trim()) {
            toast.error('Patient name is required');
            if (modalBodyRef.current) modalBodyRef.current.scrollTop = 0;
            return;
        }

        if (formData.items.length === 0) {
            toast.error('Please add at least one service');
            return;
        }

        const { isConfirmed } = await Swal.fire({
            title: 'Save Procedure Bill?',
            html: `<div style="text-align:left">
                     <p><strong>Patient:</strong> ${formData.patient_name}</p>
                     <p><strong>Type:</strong> ${formData.patient_type}</p>
                     <p><strong>Amount:</strong> ₹${parseFloat(formData.original_final_amount || formData.total_amount || 0).toFixed(2)}</p>
                   </div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'Cancel'
        });

        if (!isConfirmed) return;

        setSaving(true);
        Swal.fire({ title: 'Saving...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        try {
            const fd = new FormData();
            Object.keys(formData).forEach(key => {
                if (key !== 'items' && key !== 'services' && key !== 'created_at'
                    && key !== 'payment_status' && key !== 'paid_amount' && key !== 'payments') {
                    fd.append(key, formData[key] || '');
                }
            });
            fd.append('items', JSON.stringify(formData.items));

            try {
                const stored = sessionStorage.getItem("ldata");
                if (stored) {
                    const u = JSON.parse(stored);
                    fd.append('logged_user_id', u.user_id || '');
                    fd.append('opd_user', u.user_id || '');
                }
            } catch (e) {
                console.warn("attach user failed:", e);
            }

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.saveProcedureBillUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            Swal.close();

            if (response?.data?.status) {
                toast.success('Procedure bill saved successfully');
                const newBillId = response.data.procedure_bill_id || response.data.id;

                if (newBillId) await loadBillById(newBillId);

                fetchBillListForPatient();
                if (typeof onSaved === 'function') onSaved();
            } else {
                toast.error(response?.data?.message || 'Failed to save');
            }
        } catch (err) {
            Swal.close();
            console.error('Save error:', err);
            toast.error('Failed to save procedure bill');
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
     *  PDF
     * ============================================================ */

    const imageUrlToBase64 = async (url) => {
        if (!url) return null;
        try {
            const response = await fetch(url, { cache: 'no-cache' });
            if (!response.ok) return null;

            const blob = await response.blob();
            if (!blob.type.startsWith('image/')) return null;

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error("Error fetching or converting image (CORS?)", { url, error });
            return null;
        }
    };

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return new Date().toLocaleString();
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return new Date().toLocaleString();

            const day = String(date.getDate()).padStart(2, '0');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            const year = date.getFullYear();

            let hours = date.getHours();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const minutes = String(date.getMinutes()).padStart(2, '0');

            return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
        } catch (error) {
            return new Date().toLocaleString();
        }
    };

    const generatePDF = async (billOverride = null) => {
        const src = billOverride || formData;
        if (!src.items || src.items.length === 0) {
            toast.error('No items to print');
            return;
        }

        const doc = new jsPDF('p', 'pt', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let y = 0;

        const imageToUse = headerImageUrl || image;

        try {
            if (imageToUse) {
                const headerImageBase64 = await imageUrlToBase64(imageToUse);
                if (headerImageBase64) {
                    doc.addImage(headerImageBase64, 'PNG', 0, 0, pageWidth, 80);
                    y = 105;
                } else {
                    y = 40;
                }
            } else {
                y = 40;
            }
        } catch (error) {
            console.error('Header image error:', error);
            y = 40;
        }

        /* ---- palette (same family as the MLC receipt) ---- */
        const BRAND = [64, 81, 137];
        const LIGHT = [241, 245, 251];
        const BORDER = [214, 221, 233];
        const MUTED = [110, 118, 135];

        const typeColor = (t) => {
            switch (t) {
                case 'IPD': return [13, 110, 253];
                case 'OPD': return [25, 135, 84];
                case 'EMERGENCY': return [220, 53, 69];
                case 'CASUALTY': return [253, 126, 20];
                default: return BRAND;
            }
        };

        // TITLE driven by patient type: IPD / OPD / EMERGENCY / CASUALTY
        const ptype = normalizePatientType(
            src.patient_type || patientData?.patient_type || patientData?.bill_source || patientData?.source || ''
        );
        const billTitle = ptype ? `${ptype} PROCEDURE BILL` : 'PROCEDURE BILL';

        const M = 32;                        // page margin
        const contentW = pageWidth - M * 2;

        /* ---- TITLE BAND ---- */
        doc.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
        doc.rect(M, y, contentW, 28, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(13).setFont('helvetica', 'bold');
        doc.text(billTitle, pageWidth / 2, y + 18.5, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        y += 28;

        /* ---- PATIENT BOX ---- */
        const boxTop = y + 10;
        const boxH = 100;
        doc.setFillColor(252, 253, 255);
        doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
        doc.setLineWidth(0.7);
        doc.rect(M, boxTop, contentW, boxH, 'FD');

        const colL = M + 14;
        const colR = M + contentW / 2 + 6;
        const labelW = 84;                   // label column inside each half
        const valueW = contentW / 2 - labelW - 26;
        const rowGap = 17;
        let infoY = boxTop + 20;

        // label and value on the SAME line:  Bill No : PRO-312
        const field = (label, value, x) => {
            doc.setFontSize(9).setFont('helvetica', 'normal');
            doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
            doc.text(String(label), x, infoY);
            doc.setFontSize(9.5).setFont('helvetica', 'bold');
            doc.setTextColor(30, 30, 30);
            const lines = doc.splitTextToSize(String(value ?? '').trim() || '-', valueW);
            doc.text(`: ${lines[0]}`, x + labelW, infoY);
            doc.setTextColor(0, 0, 0);
        };

        const billDateDisplay = formatDateForDisplay(src.created_at || src.bill_date);
        const visitNoLabel = ptype === 'IPD' ? 'IPD No' : 'OPD No';

        field('Bill No', `PRO-${src.procedure_bill_id || 'NEW'}`, colL);
        field('Bill Date', billDateDisplay, colR);
        infoY += rowGap;

        field('UHID', src.ipd_bill_uhid, colL);
        field(visitNoLabel, src.ipd_no ? `000${src.ipd_no}` : '-', colR);
        infoY += rowGap;

        field('Patient Name', src.patient_name, colL);
        field('Age / Gender', `${src.patient_age || '-'} / ${src.patient_gender || '-'}`, colR);
        infoY += rowGap;

        field('Phone', src.patient_phone, colL);

        // Patient Type on the same line, value drawn as a coloured chip
        doc.setFontSize(9).setFont('helvetica', 'normal');
        doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        doc.text('Patient Type', colR, infoY);
        doc.setFontSize(9.5).setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(':', colR + labelW, infoY);
        if (ptype) {
            const tc = typeColor(ptype);
            const chipX = colR + labelW + 7;
            const chipW = doc.getTextWidth(ptype) + 14;
            doc.setFillColor(tc[0], tc[1], tc[2]);
            doc.roundedRect(chipX, infoY - 8.5, chipW, 12, 2.5, 2.5, 'F');
            doc.setTextColor(255, 255, 255);
            doc.text(ptype, chipX + 7, infoY);
        } else {
            doc.text('-', colR + labelW + 7, infoY);
        }
        doc.setTextColor(0, 0, 0);
        infoY += rowGap;

        // Address spans the full width of the box
        doc.setFontSize(9).setFont('helvetica', 'normal');
        doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        doc.text('Address', colL, infoY);
        doc.setFontSize(9.5).setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        const addrLines = doc.splitTextToSize(
            String(src.patient_address ?? '').trim() || '-',
            contentW - labelW - 40
        );
        doc.text(`: ${addrLines[0]}`, colL + labelW, infoY);
        doc.setTextColor(0, 0, 0);

        y = boxTop + boxH + 18;

        // Items Table
        const tableData = src.items.map((item, index) => [
            index + 1,
            item.service_name,
            item.qty,
            Number(item.unit_price).toFixed(2),
            Number(item.discount).toFixed(2),
            Number(item.final_price).toFixed(2)
        ]);

        autoTable(doc, {
            startY: y,
            head: [['#', 'Procedure / Service', 'Qty', 'Unit Price', 'Discount', 'Amount']],
            body: tableData,
            theme: 'grid',
            headStyles: {
                fillColor: BRAND, textColor: 255, fontStyle: 'bold',
                fontSize: 9.5, cellPadding: 6, halign: 'center'
            },
            bodyStyles: { fontSize: 9.5, cellPadding: 6, textColor: [35, 35, 35] },
            alternateRowStyles: { fillColor: [249, 251, 254] },
            styles: { lineColor: BORDER, lineWidth: 0.5 },
            columnStyles: {
                0: { halign: 'center', cellWidth: 34 },
                1: { halign: 'left' },
                2: { halign: 'center', cellWidth: 44 },
                3: { halign: 'right', cellWidth: 78 },
                4: { halign: 'right', cellWidth: 70 },
                5: { halign: 'right', cellWidth: 82, fontStyle: 'bold' }
            },
            margin: { left: M, right: M }
        });

        y = doc.lastAutoTable.finalY + 18;

        /* ---- SUMMARY BOX (right) ---- */
        const paidAmount = parseFloat(src.paid_amount || 0);
        const originalFinal = parseFloat(src.total_amount || 0) - parseFloat(src.discount_amount || 0);
        const balance = parseFloat(src.final_amount || 0);

        const sumRows = [
            ['Total Amount', Number(src.total_amount || 0).toFixed(2), false],
            [`Discount (${Number(src.discount_percent || 0)}%)`, Number(src.discount_amount || 0).toFixed(2), false],
            ['Sub Total', originalFinal.toFixed(2), true],
        ];
        if (paidAmount > 0) sumRows.push(['Paid Amount', paidAmount.toFixed(2), false]);

        const sumW = 250;
        const sumX = pageWidth - M - sumW;
        const sumH = sumRows.length * 19 + 36;

        doc.setFillColor(LIGHT[0], LIGHT[1], LIGHT[2]);
        doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
        doc.setLineWidth(0.7);
        doc.rect(sumX, y, sumW, sumH, 'FD');

        let sy = y + 18;
        sumRows.forEach(([label, val, strong]) => {
            doc.setFontSize(9.5).setFont('helvetica', strong ? 'bold' : 'normal');
            if (strong) doc.setTextColor(30, 30, 30); else doc.setTextColor(90, 95, 105);
            doc.text(label, sumX + 12, sy);
            doc.text(String(val), sumX + sumW - 12, sy, { align: 'right' });
            sy += 19;
        });
        doc.setTextColor(0, 0, 0);

        // balance due strip across the bottom of the box
        const balColor = balance > 0 ? [220, 53, 69] : [25, 135, 84];
        doc.setFillColor(balColor[0], balColor[1], balColor[2]);
        doc.rect(sumX, y + sumH - 24, sumW, 24, 'F');
        doc.setTextColor(255, 255, 255).setFontSize(11).setFont('helvetica', 'bold');
        doc.text('Balance Due', sumX + 12, y + sumH - 7.5);
        doc.text(Number(balance).toFixed(2), sumX + sumW - 12, y + sumH - 7.5, { align: 'right' });
        doc.setTextColor(0, 0, 0);

        /* ---- STATUS + REMARKS (left) ---- */
        const st = src.payment_status || 'Pending';
        const stColor = st === 'Paid' ? [25, 135, 84]
            : st === 'Partial' ? [253, 126, 20]
                : [108, 117, 125];

        doc.setFontSize(7.5).setFont('helvetica', 'normal');
        doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        doc.text('PAYMENT STATUS', M, y + 12);
        doc.setFontSize(9.5).setFont('helvetica', 'bold');
        const stW = doc.getTextWidth(st) + 18;
        doc.setFillColor(stColor[0], stColor[1], stColor[2]);
        doc.roundedRect(M, y + 18, stW, 15, 3, 3, 'F');
        doc.setTextColor(255, 255, 255);
        doc.text(st, M + 9, y + 28.5);
        doc.setTextColor(0, 0, 0);

        if (src.remarks) {
            doc.setFontSize(7.5).setFont('helvetica', 'normal');
            doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
            doc.text('REMARKS', M, y + 52);
            doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(60, 60, 60);
            const rl = doc.splitTextToSize(String(src.remarks), sumX - M - 20);
            doc.text(rl.slice(0, 3), M, y + 64);
            doc.setTextColor(0, 0, 0);
        }

        y = y + sumH + 24;

        // PAYMENT TRANSACTIONS TABLE
        try {
            const fd = new FormData();
            fd.append('procedure_bill_id', src.procedure_bill_id);

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getProcedureBillPaymentsUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response?.data?.status && response.data.payments && response.data.payments.length > 0) {
                const payments = response.data.payments;

                doc.setFontSize(10.5).setFont('helvetica', 'bold');
                doc.setTextColor(BRAND[0], BRAND[1], BRAND[2]);
                doc.text('Payment Transactions', M, y);
                doc.setTextColor(0, 0, 0);
                y += 12;

                const paymentTableData = payments.map((payment, index) => [
                    index + 1,
                    `${Number(payment.amount).toFixed(2)}`,
                    payment.payment_mode || 'Cash',
                    payment.received_by_name || 'Unknown',
                    payment.payment_date ? formatDateForDisplay(payment.payment_date) : ''
                ]);

                autoTable(doc, {
                    startY: y,
                    head: [['#', 'Amount', 'Mode', 'Received By', 'Date & Time']],
                    body: paymentTableData,
                    theme: 'grid',
                    headStyles: {
                        fillColor: BRAND, textColor: 255, fontStyle: 'bold',
                        fontSize: 9, cellPadding: 5, halign: 'center'
                    },
                    bodyStyles: { fontSize: 9, cellPadding: 5, textColor: [35, 35, 35] },
                    alternateRowStyles: { fillColor: [249, 251, 254] },
                    styles: { lineColor: BORDER, lineWidth: 0.5 },
                    columnStyles: {
                        0: { halign: 'center', cellWidth: 30 },
                        1: { halign: 'right', cellWidth: 78, fontStyle: 'bold' },
                        2: { halign: 'center', cellWidth: 80 },
                        3: { halign: 'left' },
                        4: { halign: 'left', cellWidth: 115 }
                    },
                    margin: { left: M, right: M }
                });

                y = doc.lastAutoTable.finalY + 18;
            } else {
                doc.setFontSize(9).setFont('helvetica', 'italic');
                doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
                doc.text('No payment transactions recorded', M, y);
                doc.setTextColor(0, 0, 0);
                y += 18;
            }
        } catch (error) {
            console.error('Error fetching payment transactions for PDF:', error);
            doc.setFontSize(9).setFont('helvetica', 'italic');
            doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
            doc.text('Payment transactions unavailable', M, y);
            doc.setTextColor(0, 0, 0);
            y += 18;
        }

        /* ---- FOOTER ---- */
        const footY = pageHeight - 60;
        doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]).setLineWidth(0.7);
        doc.line(pageWidth - M - 170, footY, pageWidth - M, footY);
        doc.setFontSize(9.5).setFont('helvetica', 'bold').setTextColor(40, 40, 40);
        doc.text('Authorized Signature', pageWidth - M, footY + 14, { align: 'right' });

        doc.setFontSize(7.5).setFont('helvetica', 'normal');
        doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        doc.text(`Printed on ${formatDateForDisplay(new Date().toISOString())}`, M, footY + 14);
        doc.setTextColor(0, 0, 0);

        const pdfBlob = doc.output('blob');
        window.open(URL.createObjectURL(pdfBlob), '_blank');
    };

    /* ============================================================
     *  SELECT STYLES
     * ============================================================ */

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '42px',
            borderColor: '#ced4da',
            '&:hover': { borderColor: '#86b7fe' }
        }),
        menu: (provided) => ({ ...provided, zIndex: 9999 }),
        menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f1ff' : 'white',
            color: state.isSelected ? 'white' : '#212529',
            cursor: 'pointer',
            padding: '10px 12px'
        })
    };

    const formatOptionLabel = ({ label, price }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{label}</span>
            <span className="badge bg-success ms-2">₹{price.toFixed(2)}</span>
        </div>
    );

    const typeBadgeColor = (t) => {
        switch (t) {
            case 'IPD': return '#0d6efd';
            case 'OPD': return '#198754';
            case 'EMERGENCY': return '#dc3545';
            case 'CASUALTY': return '#fd7e14';
            default: return '#6c757d';
        }
    };

    /* ============================================================
     *  RENDER
     * ============================================================ */

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            {/* ✅ near full-screen dialog */}
            <div
                className="modal-dialog modal-dialog-scrollable"
                style={{
                    maxWidth: '96vw',
                    width: '96vw',
                    height: '94vh',
                    margin: '3vh auto'
                }}
            >
                <div className="modal-content" style={{ height: '100%' }}>

                    <div className="modal-header">
                        <h5 className="modal-title d-flex align-items-center flex-wrap gap-2">
                            {formData.procedure_bill_id ? 'Edit Procedure Bill' : 'New Procedure Bill'}

                            {formData.patient_type && (
                                <span className="badge" style={{ background: typeBadgeColor(formData.patient_type) }}>
                                    {formData.patient_type}
                                </span>
                            )}

                            {formData.procedure_bill_id && formData.payment_status && (
                                <span className={`badge ${formData.payment_status === 'Paid' ? 'bg-success' : formData.payment_status === 'Partial' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                    {formData.payment_status}
                                </span>
                            )}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body" ref={modalBodyRef}>
                        {isLoadingBill ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading procedure bill...</p>
                            </div>
                        ) : (
                            <>
                                {/* ===== PREVIOUS BILLS ===== */}
                                {(billList.length > 0 || loadingBillList) && (
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="card-title mb-0">
                                                    <i className="ri-file-list-3-line me-1"></i>
                                                    Previous Procedure Bills ({billList.length})
                                                </h6>
                                                <button type="button" className="btn btn-sm btn-outline-primary"
                                                    onClick={startNewBill}>
                                                    <i className="ri-add-line me-1"></i>New Bill
                                                </button>
                                            </div>

                                            {loadingBillList ? (
                                                <div className="text-center py-2">
                                                    <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                                                    Loading bills...
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-bordered mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{ width: '50px' }}>#</th>
                                                                <th>Bill No</th>
                                                                <th>Type</th>
                                                                <th>Patient</th>
                                                                <th>UHID</th>
                                                                <th>Age/Gender</th>
                                                                <th>Date &amp; Time</th>
                                                                <th className="text-end">Amount</th>
                                                                <th>Status</th>
                                                                <th style={{ width: '130px' }}>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {billList.map((b, i) => {
                                                                const isOpen = String(b.procedure_bill_id) === String(formData.procedure_bill_id);
                                                                const amt = Number(
                                                                    b.original_final_amount ??
                                                                    b.final_amount ??
                                                                    b.total_amount ?? 0
                                                                );
                                                                const st = b.payment_status || 'Pending';
                                                                const bt = normalizePatientType(b.patient_type);
                                                                return (
                                                                    <tr key={b.procedure_bill_id || i}
                                                                        style={isOpen ? { background: '#eef4ff' } : {}}>
                                                                        <td>{i + 1}</td>
                                                                        <td><strong>PRO-{b.procedure_bill_id}</strong></td>
                                                                        <td>
                                                                            {bt ? (
                                                                                <span className="badge" style={{ background: typeBadgeColor(bt) }}>{bt}</span>
                                                                            ) : '-'}
                                                                        </td>
                                                                        <td>{b.patient_name || '-'}</td>
                                                                        <td>{b.ipd_bill_uhid || '-'}</td>
                                                                        <td>{b.patient_age || '-'} / {b.patient_gender || '-'}</td>
                                                                        <td>{formatDateForDisplay(b.created_at || b.bill_date)}</td>
                                                                        <td className="text-end">₹{amt.toFixed(2)}</td>
                                                                        <td>
                                                                            <span className={`badge ${st === 'Paid' ? 'bg-success' :
                                                                                st === 'Partial' ? 'bg-warning text-dark' : 'bg-secondary'
                                                                                }`}>{st}</span>
                                                                        </td>
                                                                        <td>
                                                                            <button type="button"
                                                                                className="btn btn-sm btn-outline-warning me-1"
                                                                                title="Edit this bill"
                                                                                onClick={() => loadBillById(b.procedure_bill_id)}>
                                                                                <i className="ri-pencil-line"></i>
                                                                            </button>
                                                                            <button type="button"
                                                                                className="btn btn-sm btn-outline-dark"
                                                                                title="Print this bill"
                                                                                onClick={async () => {
                                                                                    const loaded = await loadBillById(b.procedure_bill_id);
                                                                                    if (loaded) generatePDF(loaded);
                                                                                }}>
                                                                                <i className="ri-printer-line"></i>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger ms-1"
                                                                                onClick={() => handleDeleteProcedureBill(b)}
                                                                                style={{ opacity: isAdminUser ? 1 : 0.5 }}
                                                                                disabled={!isAdminUser}
                                                                                title={isAdminUser ? 'Delete Procedure Bill' : 'Only admin can delete'}>
                                                                                <i className="ri-delete-bin-5-fill"></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ===== PATIENT DETAILS ===== */}
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h6 className="card-title">Patient Details</h6>

                                        {/* standalone: search an existing patient */}
                                        {isStandalone && (
                                            <div className="row mb-3">
                                                <div className="col-md-6" style={{ position: 'relative' }}>
                                                    <label className="form-label">
                                                        Search Patient
                                                        <small className="text-muted ms-2" style={{ fontSize: 11 }}>
                                                            (UHID / Phone / Name — or just type the details below)
                                                        </small>
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text"><i className="ri-search-line" /></span>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Type at least 3 characters..."
                                                            value={patientSearch}
                                                            onChange={(e) => runPatientSearch(e.target.value)}
                                                            autoComplete="off"
                                                        />
                                                        {searchingPatient && (
                                                            <span className="input-group-text">
                                                                <span className="spinner-border spinner-border-sm text-primary" />
                                                            </span>
                                                        )}
                                                    </div>

                                                    {patientResults.length > 0 && (
                                                        <ul style={{
                                                            position: 'absolute', top: '100%', left: 12, right: 12,
                                                            zIndex: 1060, maxHeight: 260, overflowY: 'auto',
                                                            border: '1px solid #dee2e6', borderRadius: 4,
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                            margin: '4px 0 0 0', padding: 0, listStyle: 'none',
                                                            background: '#fff'
                                                        }}>
                                                            {patientResults.map((p, idx) => (
                                                                <li key={idx}
                                                                    onClick={() => applyPatient(p)}
                                                                    style={{
                                                                        cursor: 'pointer', fontSize: 12, padding: '10px 12px',
                                                                        borderBottom: '1px solid #f0f0f0'
                                                                    }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                                                    <strong style={{ color: '#405189' }}>
                                                                        {p.lab_reg_name_title} {p.lab_reg_name}
                                                                    </strong>
                                                                    <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                                                        <span className="me-2"><strong>{p.lab_reg_uhid}</strong></span>
                                                                        <span className="me-2">{p.lab_reg_mobile}</span>
                                                                        <span>{p.lab_reg_age_year ? p.lab_reg_age_year + 'Y' : ''} / {p.lab_reg_gender}</span>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="row">
                                            {/* ✅ PATIENT TYPE - mandatory */}
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">
                                                        Patient Type <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        className={`form-control ${!formData.patient_type ? 'border-danger' : ''}`}
                                                        value={formData.patient_type}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, patient_type: e.target.value }))}
                                                    >
                                                        <option value="">-- Select Type --</option>
                                                        {PATIENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                    <small className="text-muted" style={{ fontSize: 11 }}>
                                                        {autoPatientType
                                                            ? (formData.patient_type === autoPatientType
                                                                ? `Auto-selected (${autoPatientType}) — you can change it`
                                                                : `Changed from ${autoPatientType}`)
                                                            : 'Required before saving'}
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">UHID</label>
                                                    <input type="text" className="form-control"
                                                        value={formData.ipd_bill_uhid || ''}
                                                        readOnly={!isStandalone}
                                                        style={!isStandalone ? { background: '#EEE' } : {}}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, ipd_bill_uhid: e.target.value }))} />
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Patient Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input type="text" className="form-control"
                                                        value={formData.patient_name || ''}
                                                        readOnly={!isStandalone}
                                                        style={!isStandalone ? { background: '#EEE' } : {}}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, patient_name: e.target.value }))} />
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        {formData.patient_type === 'IPD' ? 'IPD No' : 'OPD No'}
                                                    </label>
                                                    <input type="text" className="form-control"
                                                        value={formData.ipd_no ? `000${formData.ipd_no}` : ''}
                                                        readOnly={!isStandalone}
                                                        style={!isStandalone ? { background: '#EEE' } : {}}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, ipd_no: e.target.value.replace(/^0+/, '') }))} />
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="mb-3">
                                                    <label className="form-label">Age</label>
                                                    <input type="text" className="form-control"
                                                        value={formData.patient_age || ''}
                                                        readOnly={!isStandalone}
                                                        style={!isStandalone ? { background: '#EEE' } : {}}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, patient_age: e.target.value }))} />
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="mb-3">
                                                    <label className="form-label">Gender</label>
                                                    {isStandalone ? (
                                                        <select className="form-control"
                                                            value={formData.patient_gender || ''}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, patient_gender: e.target.value }))}>
                                                            <option value="">--</option>
                                                            <option value="Male">Male</option>
                                                            <option value="Female">Female</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                    ) : (
                                                        <input type="text" className="form-control"
                                                            value={formData.patient_gender || ''}
                                                            readOnly style={{ background: '#EEE' }} />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Phone</label>
                                                    <input type="text" className="form-control"
                                                        value={formData.patient_phone || ''}
                                                        readOnly={!isStandalone}
                                                        style={!isStandalone ? { background: '#EEE' } : {}}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, patient_phone: e.target.value }))} />
                                                </div>
                                            </div>

                                            <div className="col-md-5">
                                                <div className="mb-3">
                                                    <label className="form-label">Address</label>
                                                    <input type="text" className="form-control"
                                                        value={formData.patient_address || ''}
                                                        readOnly={!isStandalone}
                                                        style={!isStandalone ? { background: '#EEE' } : {}}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, patient_address: e.target.value }))} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ===== SERVICE SEARCH ===== */}
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="mb-3">
                                                    <label className="form-label">Search &amp; Select Procedure/Service</label>
                                                    <Select
                                                        options={serviceOptions}
                                                        onChange={handleServiceSelect}
                                                        onInputChange={(inputValue) => {
                                                            if (inputValue.length > 0) fetchServices(inputValue);
                                                        }}
                                                        formatOptionLabel={formatOptionLabel}
                                                        styles={customSelectStyles}
                                                        placeholder="Type to search services..."
                                                        isClearable
                                                        isSearchable
                                                        isLoading={isLoadingServices}
                                                        noOptionsMessage={() => "No services found"}
                                                        loadingMessage={() => "Loading services..."}
                                                        value={null}
                                                        menuPosition="fixed"
                                                        menuPortalTarget={document.body}
                                                    />
                                                    <small className="text-muted">
                                                        Start typing to search, click to add service to the bill
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ===== ITEMS ===== */}
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h6 className="card-title">Procedures/Services</h6>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>S.No</th>
                                                        <th>Service</th>
                                                        <th style={{ width: '80px' }}>Qty</th>
                                                        <th style={{ width: '120px' }}>Unit Price</th>
                                                        <th style={{ width: '120px' }}>Discount</th>
                                                        <th style={{ width: '120px' }}>Final</th>
                                                        <th style={{ width: '80px' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.items.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="7" className="text-center text-muted">
                                                                No services added. Search and select services above.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        formData.items.map((item, index) => (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td>{item.service_name}</td>
                                                                <td>
                                                                    <input type="number" className="form-control form-control-sm"
                                                                        value={item.qty}
                                                                        onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()} min="1" />
                                                                </td>
                                                                <td>
                                                                    <input type="number" className="form-control form-control-sm"
                                                                        value={item.unit_price}
                                                                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()} min="0" step="0.01" />
                                                                </td>
                                                                <td>
                                                                    <input type="number" className="form-control form-control-sm"
                                                                        value={item.discount}
                                                                        onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()} min="0" step="0.01" />
                                                                </td>
                                                                <td>
                                                                    <input type="text" className="form-control form-control-sm"
                                                                        value={Number(item.final_price).toFixed(2)} readOnly />
                                                                </td>
                                                                <td>
                                                                    <button className="btn btn-sm btn-danger"
                                                                        onClick={() => removeItem(index)}>
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* ===== PAYMENT ===== */}
                                <div className="card">
                                    <div className="card-body">
                                        <h6 className="card-title">Payment Details</h6>

                                        <div className="row">
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Total Amount</label>
                                                    <input type="text" className="form-control"
                                                        value={formData.total_amount} readOnly
                                                        style={{ background: '#EEE' }} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Discount (%)</label>
                                                    <input type="number" className="form-control"
                                                        value={formData.discount_percent}
                                                        onChange={(e) => handleDiscountChange(e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        min="0" max="100" step="0.01" />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">Discount Amount (₹)</label>
                                                    <input type="number" className="form-control"
                                                        value={formData.discount_amount}
                                                        onChange={(e) => handleDiscountAmountChange(e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        min="0" step="0.01" />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label"><strong>Final Amount</strong></label>
                                                    <input type="text" className="form-control fw-bold"
                                                        value={formData.original_final_amount || formData.total_amount}
                                                        readOnly style={{ background: '#EEE', color: '#0d6efd' }} />
                                                </div>
                                            </div>

                                            {hasPaymentTable && (
                                                <>
                                                    <div className="col-md-3">
                                                        <div className="mb-3">
                                                            <label className="form-label"><strong>Amount Paid</strong></label>
                                                            <input type="text" className="form-control fw-bold"
                                                                value={formData.paid_amount || 0}
                                                                readOnly style={{ background: '#EEE', color: '#28a745' }} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="mb-3">
                                                            <label className="form-label"><strong>Balance Due</strong></label>
                                                            <input type="text" className="form-control fw-bold"
                                                                value={formData.final_amount} readOnly
                                                                style={{ background: '#EEE', color: formData.final_amount > 0 ? '#dc3545' : '#28a745' }} />
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {!hasPaymentTable && (
                                                <div className="col-md-4">
                                                    <div className="mb-3">
                                                        <label className="form-label">Payment Mode</label>
                                                        <select className="form-control"
                                                            value={formData.payment_mode}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, payment_mode: e.target.value }))}>
                                                            <option value="Cash">Cash</option>
                                                            <option value="Online">Online</option>
                                                            <option value="Card">Card</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Remarks</label>
                                                    <textarea className="form-control" rows="2"
                                                        value={formData.remarks}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                                        placeholder="Enter any remarks..."></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <i className="ri-close-line me-1"></i>
                            Close
                        </button>

                        {hasPaymentTable && billFetched && parseFloat(formData.final_amount) > 0 && (
                            <button type="button" className="btn btn-success"
                                onClick={handlePayBill}
                                disabled={saving || formData.items.length === 0 || isLoadingBill}>
                                <i className="ri-money-rupee-circle-line me-1"></i>
                                Pay Due
                            </button>
                        )}

                        <button type="button" className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving || formData.items.length === 0 || isLoadingBill}>
                            {saving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : (
                                formData.procedure_bill_id ? 'Update' : 'Save'
                            )}
                        </button>

                        <button type="button" className="btn btn-dark"
                            onClick={() => generatePDF()}
                            disabled={formData.items.length === 0 || isLoadingBill}>
                            <i className="ri-printer-line me-1"></i>
                            Print Bill
                        </button>
                    </div>
                </div>
            </div>

            {/* ===== PAYMENT MODAL ===== */}
            {showPaymentModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="ri-money-rupee-circle-line me-2"></i>
                                    Process Payment
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowPaymentModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Bill Number</label>
                                    <input type="text" className="form-control" value={`PRO-${formData.procedure_bill_id || 'NEW'}`} readOnly style={{ background: '#F8F9FA' }} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Patient Name</label>
                                    <input type="text" className="form-control" value={formData.patient_name} readOnly style={{ background: '#F8F9FA' }} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Total Due Amount</label>
                                    <input type="text" className="form-control fw-bold" value={`₹${parseFloat(formData.final_amount).toFixed(2)}`} readOnly style={{ background: '#EEE', color: '#0d6efd' }} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Payment Amount <span className="text-danger">*</span>
                                    </label>
                                    <input type="number" className="form-control"
                                        value={paymentData.amount}
                                        onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                                        onWheel={(e) => e.target.blur()}
                                        min="0" max={parseFloat(formData.final_amount)} step="0.01" required />
                                    <small className="text-muted">Maximum: ₹{parseFloat(formData.final_amount).toFixed(2)}</small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Payment Mode <span className="text-danger">*</span>
                                    </label>
                                    <select className="form-control"
                                        value={paymentData.payment_mode}
                                        onChange={(e) => setPaymentData(prev => ({ ...prev, payment_mode: e.target.value }))}>
                                        <option value="Cash">Cash</option>
                                        <option value="Online">Online</option>
                                        <option value="Card">Card</option>
                                        <option value="UPI">UPI</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Received By</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><i className="ri-user-line"></i></span>
                                        <input type="text" className="form-control"
                                            value={paymentData.received_by} readOnly style={{ background: '#F8F9FA' }} />
                                    </div>
                                    <small className="text-muted">Auto-filled from logged user</small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Remarks</label>
                                    <textarea className="form-control" rows="2"
                                        value={paymentData.remarks}
                                        onChange={(e) => setPaymentData(prev => ({ ...prev, remarks: e.target.value }))}
                                        placeholder="Payment remarks..."></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>
                                    <i className="ri-close-line me-1"></i>
                                    Close
                                </button>
                                <button type="button" className="btn btn-success"
                                    onClick={processPayment}
                                    disabled={saving || !paymentData.amount || parseFloat(paymentData.amount) <= 0}>
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-check-line me-1"></i>
                                            Pay Now
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProcedureBillModal;